/**
 * Minimal server-sent-events frame parser, pure and incremental.
 *
 * Feed it string chunks as they arrive; it returns complete frames. A frame
 * ends at a blank line. `event:` and `data:` fields are honoured (multiple
 * `data:` lines join with "\n"); `id:`/`retry:` are ignored; lines starting
 * with ":" are comments. CRLF and LF line endings both work.
 */
export interface SseFrame {
  event: string;
  data: string;
}

export interface SseParser {
  /** Push a chunk; returns every frame completed by it. */
  push(chunk: string): SseFrame[];
  /** Call at end of stream; returns any trailing frame not yet terminated. */
  flush(): SseFrame[];
}

export function createSseParser(): SseParser {
  let buffer = "";
  let event = "";
  let dataLines: string[] = [];
  let hasData = false;
  const out: SseFrame[] = [];

  function endFrame() {
    if (hasData || event) {
      out.push({ event: event || "message", data: dataLines.join("\n") });
    }
    event = "";
    dataLines = [];
    hasData = false;
  }

  function processLine(line: string) {
    if (line === "") {
      endFrame();
      return;
    }
    if (line.startsWith(":")) return;

    const idx = line.indexOf(":");
    let field: string;
    let value: string;
    if (idx === -1) {
      field = line;
      value = "";
    } else {
      field = line.slice(0, idx);
      value = line.slice(idx + 1);
      if (value.startsWith(" ")) value = value.slice(1);
    }

    if (field === "event") {
      event = value;
    } else if (field === "data") {
      dataLines.push(value);
      hasData = true;
    }
    // id / retry / unknown fields: ignored
  }

  return {
    push(chunk: string) {
      buffer += chunk;
      let nl: number;
      while ((nl = buffer.indexOf("\n")) !== -1) {
        let line = buffer.slice(0, nl);
        buffer = buffer.slice(nl + 1);
        if (line.endsWith("\r")) line = line.slice(0, -1);
        processLine(line);
      }
      return out.splice(0);
    },
    flush() {
      if (buffer) {
        processLine(buffer.endsWith("\r") ? buffer.slice(0, -1) : buffer);
        buffer = "";
      }
      endFrame();
      return out.splice(0);
    },
  };
}

/** Convenience for tests and one-shot parsing. */
export function parseSseChunks(chunks: string[]): SseFrame[] {
  const parser = createSseParser();
  const frames: SseFrame[] = [];
  for (const c of chunks) frames.push(...parser.push(c));
  frames.push(...parser.flush());
  return frames;
}
